import React, { useCallback, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Category, Combination, Term } from '../../Data/types';
import { TermTile } from '../TermTile/TermTile';
import './ConstellationGrid.css';

interface ConstellationGridProps {
  categories: Map<string, Category>;
  categoryOrder: string[];
  terms: Map<string, Term>;
  possibleCombinations: Combination[];
  onDragCombine: (term1Id: string, term2Id: string) => void;
  lastUnlockedTermId?: string | null;
  onTermClick?: (termId: string) => void;
}



export const ConstellationGrid: React.FC<ConstellationGridProps> = ({
  categories,
  categoryOrder,
  terms,
  onDragCombine,
  lastUnlockedTermId,
  onTermClick,
}) => {
  const [hoveredTermId, setHoveredTermId] = useState<string | null>(null);
  const hoveredTermRef = useRef<string | null>(null);
  const [draggingTermId, setDraggingTermId] = useState<string | null>(null);
  const isDraggingRef = useRef(false);

  const handleTermDragStart = useCallback((termId: string) => {
    isDraggingRef.current = true;
    setDraggingTermId(termId);
  }, []);

  const handleTermDragUpdate = useCallback((termId: string, x: number, y: number) => {
    let targetId: string | null = null;
    const elements = document.elementsFromPoint(x, y);
    for (const el of elements) {
      const tile = (el as HTMLElement).closest?.('[data-term-id]') as HTMLElement | null;
      if (tile) {
        const id = tile.dataset.termId;
        if (id && id !== termId) {
          targetId = id;
          break;
        }
      }
    }
    if (targetId !== hoveredTermRef.current) {
      hoveredTermRef.current = targetId;
      setHoveredTermId(targetId);
    }
  }, []);

  const handleTermDragEnd = useCallback((termId: string, x: number, y: number) => {
    isDraggingRef.current = false;
    setDraggingTermId(null);
    hoveredTermRef.current = null;
    setHoveredTermId(null);

    const elements = document.elementsFromPoint(x, y);
    for (const el of elements) {
      const tile = (el as HTMLElement).closest?.('[data-term-id]') as HTMLElement | null;
      if (tile) {
        const targetId = tile.dataset.termId;
        if (targetId && targetId !== termId) {
          onDragCombine(termId, targetId);
          break;
        }
      }
    }
  }, [onDragCombine]);

  // First era in categoryOrder that still has at least one locked term.
  // Locked tiles in this era show their emoji as a hint.
  const nextIncompleteEraName = useMemo(() => {
    return categoryOrder.find(eraName => {
      const catTerms = categories.get(eraName)?.terms ?? [];
      return catTerms.some(n => !terms.get(n)?.isUnlocked);
    }) ?? null;
  }, [categoryOrder, categories, terms]);

  return (
    <div className="category-grid">
      {categoryOrder.map((eraName) => {
        const category = categories.get(eraName);
        if (!category) return null;

        const categoryTerms = category.terms.map((n) => terms.get(n)!).filter(Boolean);
        const masteredCount = categoryTerms.filter((t) => t.isMastered).length;
        const isComplete = masteredCount === categoryTerms.length;

        return (
          <motion.div
            key={eraName}
            className={`category-card ${isComplete ? 'category-card--complete' : ''}`}
            layout
          >
            <div className="category-header">
              <span className="category-name">{eraName}</span>
              <span className="category-count">{masteredCount}/{categoryTerms.length}</span>
            </div>

            <div className="category-terms">
              <AnimatePresence>
                {categoryTerms.map((term) => (
                  <motion.div
                    key={term.termId}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    layout
                    style={{ position: 'relative' }}
                  >
                    {draggingTermId === term.termId && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: 10,
                          border: `1px dashed ${term.termType === 'technology' ? '#78350f' : '#3d3d5c'}`,
                          background: 'rgba(255,255,255,0.02)',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                    <TermTile
                      term={term}
                      isRecentlyUnlocked={term.termId === lastUnlockedTermId}
                      isDropTarget={term.termId === hoveredTermId}
                      isDragging={draggingTermId === term.termId}
                      showHint={eraName === nextIncompleteEraName}
                      onDragStart={handleTermDragStart}
                      onDragEnd={handleTermDragEnd}
                      onDragUpdate={handleTermDragUpdate}
                      onTap={onTermClick}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};
